import { useState } from 'react';

import { Box, Button, Chip, Stack, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';

import { Icon } from '@karehero/llama/components/atoms/Icon/Icon';
import { ProgressBar } from '@karehero/llama/components/atoms/ProgressBar/ProgressBar';

import StatusBar from '@/designs-dev/device/StatusBar';
import HomeIndicator from '@/designs-dev/device/HomeIndicator';
import ScreenRoot from '@/components/wrapped/ScreenRoot';

// Based on the production member onboarding (V1 flow, the one live for most
// orgs): care stage → personalisation goals → feeling check-in, then straight
// to the action plan. Step order and copy mirror
// front-end/pwa/src/hooks/useOnboardingV1.ts, minus production's TOS consent
// interstitial, which this redesign drops. Production shows no Back button on
// these steps.

// Option data lifted from the production seeds (api/store/seed/care_stage.go,
// api/store/seed/personalization.go) and the live onboarding care-profile
// question (caregiver-feeling). The icon fills are seed data values delivered
// by the API, not theme colours.
const CARE_STAGES = [
  'Planning to give care in the future',
  'Starting to care for a family member',
  'Regularly caring for a family member',
  "My family member's health is deteriorating",
];

const GOALS = [
  {
    label: 'Find professional care',
    icon: 'hand-holding-heart',
    fill: '#f18b6c',
  },
  { label: 'Cover costs of care', icon: 'money-bills', fill: '#cfe09b' },
  { label: 'Get Power of Attorney', icon: 'file-contract', fill: '#044dba' },
  { label: 'Talk to an expert', icon: 'phone', fill: '#f8defd' },
  { label: 'Get respite from care', icon: 'scale-balanced', fill: '#fcef9f' },
  { label: 'Understand where to start', icon: 'flag', fill: '#003413' },
  { label: 'Get expert resources', icon: 'heart', fill: '#883961' },
];

const FEELINGS = [
  'Stressed',
  'Overwhelmed',
  'Sad',
  'Busy',
  'Guilt',
  'Numb',
  'Anxious',
  'Exhausted',
  'Productive',
  'Positive',
  'Proud',
  'Relieved',
  'Accepting',
  'Supported',
];

// The {caregiver} placeholder production interpolates from the account.
const CAREGIVER_NAME = 'Vez';

// Seed fills are arbitrary hexes, so work out whether the icon should knock
// out white the same way llama's getColorContrast does.
const isDarkFill = (hex: string) => {
  const n = parseInt(hex.slice(1), 16);
  const r = (n >> 16) & 255;
  const g = (n >> 8) & 255;
  const b = n & 255;
  return 0.299 * r + 0.587 * g + 0.114 * b < 140;
};

const STEPS = ['care-stage', 'goals', 'feeling'] as const;
type Step = (typeof STEPS)[number];

const CurrentOnboarding = ({ onComplete }: { onComplete: () => void }) => {
  const [stepIndex, setStepIndex] = useState(0);
  const [careStage, setCareStage] = useState<string | null>(null);
  const [goals, setGoals] = useState<string[]>([]);
  const [feelings, setFeelings] = useState<string[]>([]);

  const step: Step = STEPS[stepIndex];

  const toggle = (list: string[], set: (v: string[]) => void, item: string) =>
    set(list.includes(item) ? list.filter((x) => x !== item) : [...list, item]);

  const canContinue = {
    'care-stage': !!careStage,
    goals: goals.length > 0,
    feeling: feelings.length > 0,
  }[step];

  const next = () => {
    if (stepIndex === STEPS.length - 1) onComplete();
    else setStepIndex((i) => i + 1);
  };

  return (
    <ScreenRoot>
      <StatusBar />

      <Box sx={{ px: 3, pt: 2, pb: 1, flexShrink: 0 }}>
        <ProgressBar limit={3} progress={stepIndex} label='Progress' />
      </Box>

      <Box
        sx={{
          flex: 1,
          overflow: 'auto',
          px: 3,
          pt: 1,
          pb: 2,
          display: 'flex',
          flexDirection: 'column',
          minHeight: 0,
        }}
      >
        {step === 'care-stage' && (
          <Stack sx={{ gap: 3, pt: 2 }}>
            <Typography variant='h2'>Where are you in your journey?</Typography>
            <Stack sx={{ gap: 1.5 }}>
              {CARE_STAGES.map((stage) => {
                const selected = careStage === stage;
                return (
                  <Box
                    key={stage}
                    onClick={() => setCareStage(stage)}
                    sx={[
                      {
                        border: 1,
                        borderColor: selected
                          ? 'primary.main'
                          : 'custom.neutral.300',
                        borderRadius: '8px',
                        px: 2,
                        py: 2,
                      },
                      (theme) => ({
                        cursor: 'pointer',
                        bgcolor: selected
                          ? alpha(theme.palette.primary.main, 0.08)
                          : 'transparent',
                      }),
                    ]}
                  >
                    <Typography variant='body1'>{stage}</Typography>
                  </Box>
                );
              })}
            </Stack>
          </Stack>
        )}

        {step === 'goals' && (
          <Stack sx={{ gap: 3, pt: 2 }}>
            <Stack sx={{ gap: 0.5 }}>
              <Typography variant='h2'>What can we help you with?</Typography>
              <Typography variant='body2' color='text.secondary'>
                Please select as many as you need
              </Typography>
            </Stack>
            <Stack sx={{ gap: 1.5 }}>
              {GOALS.map((goal) => {
                const selected = goals.includes(goal.label);
                return (
                  <Stack
                    key={goal.label}
                    direction='row'
                    onClick={() => toggle(goals, setGoals, goal.label)}
                    sx={[
                      {
                        alignItems: 'center',
                        gap: 1.5,
                        border: 1,
                        borderColor: selected
                          ? 'primary.main'
                          : 'custom.neutral.300',
                        borderRadius: '8px',
                        px: 2,
                        py: 1.5,
                      },
                      (theme) => ({
                        cursor: 'pointer',
                        bgcolor: selected
                          ? alpha(theme.palette.primary.main, 0.08)
                          : 'transparent',
                      }),
                    ]}
                  >
                    <Box
                      sx={{
                        width: 32,
                        height: 32,
                        borderRadius: '50%',
                        bgcolor: goal.fill,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                        color: isDarkFill(goal.fill)
                          ? 'common.white'
                          : 'text.primary',
                      }}
                    >
                      <Icon icon={goal.icon as never} fontSize={14} />
                    </Box>
                    <Typography variant='body1'>{goal.label}</Typography>
                  </Stack>
                );
              })}
            </Stack>
          </Stack>
        )}

        {step === 'feeling' && (
          <Stack sx={{ gap: 3, pt: 2 }}>
            <Typography variant='h2'>
              Thanks {CAREGIVER_NAME}, and how are you feeling today?
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {FEELINGS.map((feeling) => {
                const selected = feelings.includes(feeling);
                return (
                  <Chip
                    key={feeling}
                    label={feeling}
                    clickable
                    color={selected ? 'primary' : 'default'}
                    variant={selected ? 'filled' : 'outlined'}
                    onClick={() => toggle(feelings, setFeelings, feeling)}
                  />
                );
              })}
            </Box>
          </Stack>
        )}
      </Box>

      {/* production's pinned action bar: single full-width primary action */}
      <Box
        sx={{
          borderTop: 1,
          borderColor: 'custom.neutral.200',
          px: 2,
          py: 2,
          flexShrink: 0,
        }}
      >
        <Button
          aria-label='Continue'
          variant='contained'
          fullWidth
          disabled={!canContinue}
          onClick={next}
        >
          Continue
        </Button>
      </Box>

      <HomeIndicator />
    </ScreenRoot>
  );
};

export default CurrentOnboarding;
